// components/socials/Feed.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { FiPlus } from "react-icons/fi";

import { db } from "../../firebase/firebase.utils";
import PostDetail from "./PostDetail";
import "./feed.styles.scss";

const PAGE_SIZE = 5;
const QUERY_SIZE = PAGE_SIZE * 3;
const AUTHOR_QUERY_SIZE = 30;

const cleanArray = (value) =>
  Array.isArray(value) ? value.filter(Boolean) : [];
const key = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const log1p = (value) => Math.log(1 + Math.max(0, Number(value || 0)));

const recencyScore = (createdAt) => {
  const timestamp = createdAt?.toMillis?.() || createdAt?.seconds * 1000 || 0;
  if (!timestamp) return 0;
  return Math.exp(-Math.max(0, (Date.now() - timestamp) / 3600000) / 36);
};

const isMuted = (post, preferences) => {
  if (post?.moderationHidden === true) return true;
  const topics = new Set(cleanArray(preferences?.mutedTopics));
  const tags = new Set(cleanArray(preferences?.mutedTags).map(key));
  return (
    topics.has(post.type) ||
    cleanArray(post.tags).some((tag) => tags.has(key(tag)))
  );
};

const scorePost = (post, preferences) => {
  const signals = preferences?.postSignals || {};
  const tagAffinity = cleanArray(post.tags)
    .slice(0, 6)
    .reduce((sum, tag) => sum + Number(signals.tagScore?.[key(tag)] || 0), 0);

  return (
    10 * recencyScore(post.createdAt) +
    0.35 * log1p(post.likeCount) +
    0.55 * log1p(post.commentCount) +
    0.45 * log1p(post.saveCount) +
    0.9 * Number(signals.topicScore?.[post.type] || 0) +
    0.6 * tagAffinity +
    0.7 * Number(signals.authorScore?.[post.authorId] || 0) -
    (signals.seen?.[post.id] ? 2 : 0)
  );
};

const getStrategy = (preferences) => {
  const topics = cleanArray(preferences?.feedTopics).slice(0, 10);
  const tags = cleanArray(preferences?.followedTags).map(key).slice(0, 10);
  if (topics.length) return { mode: "topics", topics, tags: [] };
  if (tags.length) return { mode: "tags", topics: [], tags };
  return { mode: "latest", topics: [], tags: [] };
};

export default function Feed({
  display = "flex",
  initialPostId = null,
  authorId = null,
  authorOverride = null,
  isOwnProfile = false,
  isConnected = false,
  hidePostHeaders = false,
  profileViewer = false,
  onExitProfileViewer,
  onOpenAuthor,
  onCreate,
  refreshToken = 0,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const uid = currentUser?.uid;
  const visible = display !== "none";

  const [preferences, setPreferences] = useState(undefined);
  const [postIds, setPostIds] = useState([]);
  const [posts, setPosts] = useState({});
  const [authors, setAuthors] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const feedRef = useRef(null);
  const authorsRef = useRef({});
  const updatePost = useCallback(
    (post) =>
      setPosts((previous) =>
        previous[post.id] === post
          ? previous
          : { ...previous, [post.id]: post },
      ),
    [],
  );
  const itemRefs = useRef({});
  const cursorRef = useRef(null);
  const candidateBufferRef = useRef([]);
  const postIdsRef = useRef([]);
  const preferencesRef = useRef(null);
  const strategyRef = useRef(null);
  const fetchingRef = useRef(false);
  const requestRef = useRef(0);
  const authorExhaustedRef = useRef(false);
  const authorOverrideRef = useRef(authorOverride);
  authorOverrideRef.current = authorOverride;
  const visibleRef = useRef(visible);

  useEffect(() => {
    postIdsRef.current = postIds;
  }, [postIds]);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    if (!fullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreen]);

  useEffect(() => {
    if (!uid) {
      setPreferences(null);
      preferencesRef.current = null;
      return undefined;
    }

    return db
      .collection("users")
      .doc(uid)
      .onSnapshot(
        (snapshot) => {
          const next = snapshot.exists ? snapshot.data() : null;
          preferencesRef.current = next;
          setPreferences(next);
        },
        (error) => {
          console.log("Feed preferences snapshot error:", error);
          preferencesRef.current = null;
          setPreferences(null);
        },
      );
  }, [uid]);

  const preferenceKey = useMemo(
    () =>
      JSON.stringify({
        topics: cleanArray(preferences?.feedTopics),
        followedTags: cleanArray(preferences?.followedTags),
        mutedTopics: cleanArray(preferences?.mutedTopics),
        mutedTags: cleanArray(preferences?.mutedTags),
      }),
    [
      preferences?.feedTopics,
      preferences?.followedTags,
      preferences?.mutedTopics,
      preferences?.mutedTags,
    ],
  );

  const fetchPage = useCallback(
    async (reset = false) => {
      if (!visibleRef.current || (!reset && fetchingRef.current)) return;
      const request = reset ? ++requestRef.current : requestRef.current;
      const isCurrent = () => request === requestRef.current;
      fetchingRef.current = true;
      setFetchError("");
      reset ? setLoading(true) : setLoadingMore(true);

      try {
        const strategy = reset
          ? getStrategy(preferencesRef.current)
          : strategyRef.current || getStrategy(preferencesRef.current);
        if (reset) {
          strategyRef.current = strategy;
          cursorRef.current = null;
          candidateBufferRef.current = [];
          postIdsRef.current = [];
          authorExhaustedRef.current = false;
          setPostIds([]);
          setPosts({});
          setActiveIndex(0);
          setHasMore(true);
          feedRef.current?.scrollTo({ top: 0, behavior: "auto" });
        }

        const existing = new Set(postIdsRef.current);
        let candidates = [...candidateBufferRef.current];
        let cursor = cursorRef.current;
        let authorExhausted = authorExhaustedRef.current;
        let snapshot = null;

        if (authorId) {
          // Match the profile grid's privacy constraints and date order.
          while (candidates.length < PAGE_SIZE && !authorExhausted) {
            let query = db
              .collection("posts")
              .where("authorId", "==", authorId);
            if (!isOwnProfile)
              query = isConnected
                ? query.where("visibility", "in", ["public", "connections"])
                : query.where("visibility", "==", "public");
            query = query.orderBy("createdAt", "desc");
            if (cursor) query = query.startAfter(cursor);
            snapshot = await query.limit(AUTHOR_QUERY_SIZE).get();
            if (!isCurrent()) return;
            cursor = snapshot.docs[snapshot.docs.length - 1] || cursor;
            authorExhausted = snapshot.size < AUTHOR_QUERY_SIZE;
            candidates.push(
              ...snapshot.docs
                .map((item) => ({ ...item.data(), id: item.id }))
                .filter((post) => !post.isDeleted && post.moderationHidden !== true && !existing.has(post.id)),
            );
          }
        } else if (candidates.length < PAGE_SIZE) {
          let query = db
            .collection("posts")
            .where("visibility", "==", "public");
          if (strategy.mode === "topics")
            query = query.where("type", "in", strategy.topics);
          if (strategy.mode === "tags")
            query = query.where("tags", "array-contains-any", strategy.tags);
          query = query.orderBy("createdAt", "desc");
          if (cursor) query = query.startAfter(cursor);
          snapshot = await query.limit(QUERY_SIZE).get();
          if (!isCurrent()) return;
          if (!snapshot.empty) {
            cursor = snapshot.docs[snapshot.docs.length - 1];
            const fetched = snapshot.docs
              .map((item) => ({ id: item.id, ...item.data() }))
              .filter((post) => !isMuted(post, preferencesRef.current))
              .map((post) => ({
                ...post,
                _score: scorePost(post, preferencesRef.current),
              }))
              .sort((a, b) => b._score - a._score);
            candidates.push(...fetched);
          }
        }

        if (reset && initialPostId) {
          let openingPost = candidates.find(
            (post) => post.id === initialPostId,
          );
          if (!openingPost) {
            try {
              const openingSnapshot = await db
                .collection("posts")
                .doc(initialPostId)
                .get();
              if (!isCurrent()) return;
              if (openingSnapshot.exists) {
                const possiblePost = {
                  ...openingSnapshot.data(),
                  id: openingSnapshot.id,
                };
                if (
                  !possiblePost.isDeleted &&
                  (!authorId ||
                    (possiblePost.authorId === authorId &&
                      (isOwnProfile ||
                        possiblePost.visibility === "public" ||
                        (isConnected &&
                          possiblePost.visibility === "connections"))))
                ) {
                  openingPost = possiblePost;
                }
              }
            } catch (error) {
              if (!isCurrent()) return;
              if (error.code !== "permission-denied") throw error;
            }
          }
          if (authorId && !openingPost) {
            setHasMore(false);
            setFetchError("This post is unavailable.");
            return;
          }
          if (openingPost)
            candidates = [
              openingPost,
              ...candidates.filter((post) => post.id !== openingPost.id),
            ];
        }

        const uniqueCandidates = [
          ...new Map(candidates.map((post) => [post.id, post])).values(),
        ].filter((post) => !existing.has(post.id));
        const selected = uniqueCandidates.slice(0, PAGE_SIZE);
        if (!selected.length) {
          cursorRef.current = cursor;
          authorExhaustedRef.current = authorExhausted;
          candidateBufferRef.current = [];
          setHasMore(false);
          return;
        }
        const authorOverride = authorOverrideRef.current;
        const missingAuthorIds =
          authorOverride && authorId
            ? []
            : [
                ...new Set(
                  selected
                    .map((post) => post.authorId)
                    .filter(
                      (id) =>
                        id &&
                        !Object.prototype.hasOwnProperty.call(
                          authorsRef.current,
                          id,
                        ),
                    ),
                ),
              ];
        const authorEntries =
          authorOverride && authorId
            ? [[authorId, authorOverride]]
            : await Promise.all(
                missingAuthorIds.map(async (missingAuthorId) => {
                  const authorSnapshot = await db
                    .collection("users")
                    .doc(missingAuthorId)
                    .get();
                  return authorSnapshot.exists
                    ? [
                        missingAuthorId,
                        { id: authorSnapshot.id, ...authorSnapshot.data() },
                      ]
                    : [missingAuthorId, null];
                }),
              );
        if (!isCurrent()) return;

        cursorRef.current = cursor;
        authorExhaustedRef.current = authorExhausted;
        candidateBufferRef.current = uniqueCandidates.slice(PAGE_SIZE);
        setPosts((previous) => ({
          ...previous,
          ...Object.fromEntries(selected.map((post) => [post.id, post])),
        }));
        Object.assign(authorsRef.current, Object.fromEntries(authorEntries));
        setAuthors((previous) => ({
          ...previous,
          ...Object.fromEntries(authorEntries.filter(([, author]) => author)),
        }));
        const nextIds = [
          ...postIdsRef.current,
          ...selected.map((post) => post.id),
        ];
        postIdsRef.current = nextIds;
        setPostIds(nextIds);
        const databaseMayHaveMore = authorId
          ? !authorExhausted
          : snapshot
            ? snapshot.size === QUERY_SIZE
            : true;
        setHasMore(
          candidateBufferRef.current.length > 0 || databaseMayHaveMore,
        );
      } catch (error) {
        if (isCurrent()) {
          console.log("Feed fetch error:", error);
          setFetchError("Could not load posts. Please try again.");
        }
      } finally {
        if (isCurrent()) {
          fetchingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [initialPostId, authorId, isOwnProfile, isConnected, uid],
  );

  const feedPreferenceKey = authorId ? "" : preferenceKey;
  useEffect(() => {
    if (!visible || preferences === undefined) return;
    fetchPage(true);
    return () => {
      // Discard results from the previous profile/connection state.
      requestRef.current += 1;
      fetchingRef.current = false;
    };
  }, [
    visible,
    feedPreferenceKey,
    preferences === undefined,
    refreshToken,
    fetchPage,
  ]);

  useEffect(() => {
    if (!visible || !postIds.length || !feedRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visibleEntry)
          setActiveIndex(Number(visibleEntry.target.dataset.index));
      },
      { root: feedRef.current, threshold: [0.6, 0.75] },
    );

    Object.values(itemRefs.current).forEach(
      (item) => item && observer.observe(item),
    );
    return () => observer.disconnect();
  }, [visible, postIds, fullscreen]);

  useEffect(() => {
    if (!feedRef.current || !postIds.length) return;

    requestAnimationFrame(() => {
      const scroller = feedRef.current;
      scroller?.scrollTo({
        top: activeIndex * scroller.clientHeight,
        behavior: "auto",
      });
    });
  }, [fullscreen]);

  const handleScroll = (event) => {
    const element = event.currentTarget;
    if (
      hasMore &&
      !loading &&
      !loadingMore &&
      !fetchError &&
      element.scrollTop + element.clientHeight >= element.scrollHeight - 250
    ) {
      fetchPage(false);
    }
  };

  const removePostLocally = (postId) => {
    const next = postIdsRef.current.filter((id) => id !== postId);
    postIdsRef.current = next;
    setPostIds(next);
    setActiveIndex((index) => Math.min(index, Math.max(0, next.length - 1)));
  };

  const feedContent = (
    <section
      className={`social-feed ${fullscreen ? "social-feed--fullscreen" : ""} ${
        hidePostHeaders ? "social-feed--hide-post-headers" : ""
      } ${profileViewer ? "social-feed--profile-viewer" : ""}`}
      style={{ display }}
      onClick={
        fullscreen
          ? (event) => {
              if (event.target === event.currentTarget) setFullscreen(false);
            }
          : undefined
      }
    >
      {onCreate && (
        <button
          className="social-feed__create"
          type="button"
          onClick={onCreate}
          aria-label="Create post"
        >
          <FiPlus />
        </button>
      )}
      <div
        className="social-feed__scroller"
        ref={feedRef}
        onScroll={handleScroll}
      >
        {loading && <div className="social-feed__state">Loading feed…</div>}
        {!loading && !postIds.length && !fetchError && (
          <div className="social-feed__state">
            No posts yet.
            {profileViewer && (
              <button type="button" onClick={onExitProfileViewer}>
                Back to all posts
              </button>
            )}
          </div>
        )}

        {postIds.map((postId, index) => {
          const post = posts[postId];
          return (
            <div
              className="social-feed__item"
              data-index={index}
              key={postId}
              ref={(element) => {
                itemRefs.current[postId] = element;
              }}
            >
              {Math.abs(index - activeIndex) <= 1 && visible && (
                <PostDetail
                  postId={postId}
                  initialPost={post}
                  initialAuthor={post?.authorId ? authors[post.authorId] : null}
                  active={visible && index === activeIndex}
                  inFeed
                  prepareMedia={visible}
                  preloadMedia={index === activeIndex + 1 ? "auto" : "metadata"}
                  onPostUpdated={updatePost}
                  fullscreen={fullscreen}
                  profileViewer={profileViewer}
                  onToggleFullscreen={() => setFullscreen((value) => !value)}
                  onExitProfileViewer={onExitProfileViewer}
                  onOpenAuthor={onOpenAuthor}
                  onDeleted={removePostLocally}
                />
              )}
            </div>
          );
        })}

        {loadingMore && (
          <div className="social-feed__loading-more">Loading…</div>
        )}
        {!!fetchError && (
          <div
            className={
              postIds.length
                ? "social-feed__loading-more"
                : "social-feed__state"
            }
          >
            <p>{fetchError}</p>
            <button
              type="button"
              onClick={() => fetchPage(!postIdsRef.current.length)}
            >
              Try again
            </button>
            {profileViewer && (
              <button type="button" onClick={onExitProfileViewer}>
                Back to all posts
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );

  return fullscreen ? createPortal(feedContent, document.body) : feedContent;
}
